import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Settings, Crown, Users, Heart, Save, 
  AlertTriangle, Check, RefreshCw, MessageSquare, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminLayout from "@/components/AdminLayout";
import AdminChangePassword from "@/components/admin/AdminChangePassword";

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("subscriptions");

  // Updated subscription tiers (Recovery-focused)
  const subscriptionTiers = [
    {
      id: "free",
      name: "Free",
      price: 0,
      features: ["3 matches/day", "10 messages per match", "Basic profile", "Community support"]
    },
    {
      id: "recovery_plus",
      name: "Recovery+",
      price: 9.99,
      features: ["10 matches/day", "Unlimited messaging", "Profile insights", "See who viewed you", "Read receipts"]
    },
    {
      id: "recovery_elite",
      name: "Recovery Elite",
      price: 19.99,
      features: ["Unlimited matches", "Advanced filters", "Video calls", "Priority visibility", "Incognito mode", "Dedicated support"]
    }
  ];

  const [migrationStatus, setMigrationStatus] = useState(null);
  
  const handleMigrateSubscriptionTiers = async () => {
    if (!window.confirm("This will migrate all legacy subscription tier names (platinum→recovery_elite, gold→recovery_plus, basic→free). Continue?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('recoverymatch_admin_token');
      const response = await fetch('/api/admin/migrate-subscription-tiers', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setMigrationStatus(data);
        alert(`Migration complete!\n\nPlatinum → Elite: ${data.details.platinum_to_elite}\nGold → Plus: ${data.details.gold_to_plus}\nBasic → Free: ${data.details.basic_to_free}\n\nTotal: ${data.details.total_migrated} users migrated`);
      } else {
        alert("Migration failed: " + (data.detail || data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Migration error:", error);
      alert("Migration failed: " + error.message);
    }
  };

  // Match settings
  const matchSettings = [
    { setting: "Match Expiry Time", value: "72 hours", description: "Time before an unactioned match expires" },
    { setting: "Guided Prompts Required", value: "3 exchanges", description: "Messages before freeform chat unlocks" },
    { setting: "Daily Match Refresh", value: "6:00 AM UTC", description: "When new daily matches are generated" },
    { setting: "Compatibility Threshold", value: "60%", description: "Minimum score to show as a match" }
  ];

  // Message features by tier
  const messageFeatures = {
    "Free": [
      { feature: "Messages per match", value: "10" },
      { feature: "Guided prompts", value: "Yes" },
      { feature: "Freeform chat", value: "After 3 exchanges" },
      { feature: "Read receipts", value: "No" },
      { feature: "Typing indicators", value: "No" }
    ],
    "Recovery+": [
      { feature: "Messages per match", value: "Unlimited" },
      { feature: "Guided prompts", value: "Yes" },
      { feature: "Freeform chat", value: "After 3 exchanges" },
      { feature: "Read receipts", value: "Yes" },
      { feature: "Typing indicators", value: "Yes" }
    ],
    "Recovery Elite": [
      { feature: "Messages per match", value: "Unlimited" },
      { feature: "Guided prompts", value: "Optional" },
      { feature: "Freeform chat", value: "Immediate" },
      { feature: "Read receipts", value: "Yes" },
      { feature: "Typing indicators", value: "Yes" },
      { feature: "Video calls", value: "Yes" }
    ]
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 500);
  }, []);

  const tabs = [
    { id: "subscriptions", label: "Subscriptions", icon: Crown },
    { id: "matching", label: "Matching", icon: Heart },
    { id: "messaging", label: "Messaging", icon: MessageSquare },
    { id: "security", label: "Security", icon: Shield }
  ];

  if (loading) {
    return (
      <AdminLayout>
      <div className="bg-gray-50 flex items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold text-[#1C3A7A]">App Settings</h1>
                <p className="text-sm text-gray-500">Configuration & pricing</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#1C3A7A] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Configuration Reference</p>
            <p className="text-sm text-blue-600 mt-1">
              These settings are managed via RevenueCat and backend configuration. This page provides a reference view of current settings.
            </p>
          </div>
        </div>

        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1C3A7A] flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#FAB710]" />
                Subscription Tiers
              </h3>
              <Button
                onClick={handleMigrateSubscriptionTiers}
                variant="outline"
                size="sm"
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Migrate Legacy Tiers
              </Button>
            </div>
            
            {migrationStatus && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <Check className="w-4 h-4 inline mr-2" />
                Last migration: {migrationStatus.details.total_migrated} users updated
              </div>
            )}
            
            <div className="grid md:grid-cols-3 gap-4">
              {subscriptionTiers.map((tier) => (
                <div 
                  key={tier.id}
                  className={`bg-white rounded-xl p-5 shadow-sm border-2 ${
                    tier.id === 'recovery_elite' ? 'border-[#1C3A7A]' : 
                    tier.id === 'recovery_plus' ? 'border-[#FAB710]' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-lg text-[#1C3A7A]">{tier.name}</h4>
                    {tier.id === 'recovery_elite' && (
                      <span className="bg-[#1C3A7A] text-white text-xs px-2 py-1 rounded-full">Best Value</span>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[#1C3A7A]">
                      ${tier.price.toFixed(2)}
                    </span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matching Tab */}
        {activeTab === "matching" && (
          <div className="space-y-6">
            <h3 className="font-semibold text-[#1C3A7A] flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Match Configuration
            </h3>
            
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Setting</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Value</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden md:table-cell">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {matchSettings.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.setting}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-[#1C3A7A]">{item.value}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">{item.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Daily Match Limits by Tier */}
            <div className="mt-6">
              <h4 className="font-medium text-gray-700 mb-3">Daily Match Limits by Tier</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm text-center">
                  <p className="text-gray-500 text-sm mb-1">Free</p>
                  <p className="text-2xl font-bold text-[#1C3A7A]">3</p>
                  <p className="text-xs text-gray-400">matches/day</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm text-center border-2 border-[#FAB710]">
                  <p className="text-[#FAB710] text-sm mb-1 font-medium">Recovery+</p>
                  <p className="text-2xl font-bold text-[#1C3A7A]">10</p>
                  <p className="text-xs text-gray-400">matches/day</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm text-center border-2 border-[#1C3A7A]">
                  <p className="text-[#1C3A7A] text-sm mb-1 font-medium">Elite</p>
                  <p className="text-2xl font-bold text-[#1C3A7A]">∞</p>
                  <p className="text-xs text-gray-400">unlimited</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messaging Tab */}
        {activeTab === "messaging" && (
          <div className="space-y-6">
            <h3 className="font-semibold text-[#1C3A7A] flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              Messaging Features by Tier
            </h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(messageFeatures).map(([tierName, features]) => (
                <div key={tierName} className="bg-white rounded-xl p-4 shadow-sm">
                  <h4 className={`font-bold mb-4 ${
                    tierName === 'Recovery Elite' ? 'text-[#1C3A7A]' :
                    tierName === 'Recovery+' ? 'text-[#FAB710]' : 'text-gray-700'
                  }`}>
                    {tierName}
                  </h4>
                  <ul className="space-y-3">
                    {features.map((item, idx) => (
                      <li key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{item.feature}</span>
                        <span className={`font-medium ${
                          item.value === 'Yes' || item.value === 'Unlimited' ? 'text-green-600' :
                          item.value === 'No' ? 'text-gray-400' : 'text-[#1C3A7A]'
                        }`}>
                          {item.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Reactions Info */}
            <div className="mt-6">
              <h4 className="font-medium text-gray-700 mb-3">Message Reactions</h4>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-sm text-gray-600 mb-3">Available to all users for free:</p>
                <div className="flex gap-4 justify-center">
                  {['❤️', '👍', '😊', '😂', '😮', '😢'].map((emoji, idx) => (
                    <span key={idx} className="text-2xl">{emoji}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab (RM-ADMIN-1) */}
        {activeTab === "security" && (
          <div className="space-y-6">
            <AdminChangePassword />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
